/**
 * Patient Auth API Service
 *
 * Handles patient authentication operations using native fetch client.
 */

import { api, setAuthToken, clearAuthToken } from './api';

// ============================================================================
// Types
// ============================================================================

export interface PatientProfile {
  participantId: string;
  projectId: string;
  status: string;
  id?: string;
  name?: string;
  external_id?: string | null;
  birth_date?: string | null;
  gender?: string | null;
  site_id?: string;
  preferred_language?: string | null;
  project_default_language?: string | null;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  participant: {
    participantId: string;
    projectId: string;
    status: string;
  };
}

export interface ActivationCodeResponse {
  resetToken: string;
  participantId: string;
}

// Session-based access types (for SMS link /s/{session_id})
export interface SessionInfoResponse {
  valid: boolean;
  error?: string;
  session?: {
    id: string;
    status: string;
    open_at: string;
    close_at: string;
  };
  survey?: {
    id: string;
    title: string;
    description?: string;
  };
  participant?: {
    id: string;
    masked_name: string;
    phone_masked?: string;
  };
  organization?: {
    code: string;
    name: string;
  };
  auth_config?: {
    default_method: string;
    available_methods: string[];
    require_birth_date: boolean;
    session_token_format?: 'numeric' | 'alphanumeric';
    session_token_length?: number;
    respondent_type?: 'PATIENT' | 'CRC' | 'BOTH';
  };
  language_config?: {
    server_language?: string;
    participant_preferred_language?: string;
  };
  modification_reason_presets?: Array<{ ko: string; en: string; id?: string }>;
  requires_otp: boolean; // deprecated - use auth_config instead
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Login with participant ID and PIN.
 */
export async function pinLogin(participantId: string, pin: string): Promise<LoginResponse> {
  const response = await api.post<LoginResponse>('/patient/auth/pin-login', {
    participantId,
    pin,
  });

  // Store the token after successful login
  setAuthToken(response.accessToken);

  return response;
}

/**
 * Fetch the current patient's profile.
 */
export async function fetchProfile(): Promise<PatientProfile> {
  return api.get<PatientProfile>('/patient/auth/profile');
}

/**
 * Verify an activation code.
 */
export async function verifyActivationCode(activationCode: string): Promise<ActivationCodeResponse> {
  return api.post<ActivationCodeResponse>('/patient/auth/activation-code/verify', {
    activationCode,
  });
}

/**
 * Regenerate activation code for a session.
 */
export async function regenerateActivationCode(sessionId: string): Promise<{ success: boolean; activation_code?: string; message?: string }> {
  return api.post<{ success: boolean; activation_code?: string; message?: string }>(`/patient/sessions/${sessionId}/regenerate-activation-code`, undefined);
}

/**
 * Setup a new PIN using the reset token.
 */
export async function setupPin(resetToken: string, newPin: string): Promise<void> {
  await api.post('/patient/auth/setup-pin', {
    resetToken,
    newPin,
  });
}

/**
 * Logout the current patient.
 */
export function logout(): void {
  clearAuthToken();
}

// ============================================================================
// Session-based API Functions (for SMS link /s/{session_id})
// ============================================================================

/**
 * Get session info before activation-code verification.
 * @param sessionId - SurveyAssignment UUID from SMS link
 */
export async function getSessionInfo(sessionId: string): Promise<SessionInfoResponse> {
  return api.get<SessionInfoResponse>(`/patient/auth/session/${sessionId}`);
}

// ============================================================================
// Exports
// ============================================================================

export const authApi = {
  pinLogin,
  fetchProfile,
  verifyActivationCode,
  setupPin,
  logout,
  // Session-based (for SMS link /s/{session_id})
  getSessionInfo,
} as const;

export default authApi;
